import uuid from "uuid-random";
import { Fin } from "../fin/fin";
import { toGoLangInvoke, openUrlWithBrowser } from "../common/windowFunctions";
import { logStatus } from "../utils";
class FEAWindowPolyfill {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.asyncConstructors = asyncConstructors;
        this.fin = new Fin(gluePromise, this.asyncConstructors, loggerClient);
    }
    getCurrent() {
        return this.fin.desktop.Window.getCurrent();
    }
}
export class SystemImpl {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.protocolHandlerEventChannel = null;
        const pw = this.asyncConstructors.addConstructor();
        this.Window = new FEAWindowPolyfill(gluePromise, this.asyncConstructors, this.loggerClient);
        gluePromise.then((g) => {
            this.glue = g;
            this.asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "System",
            method, type, data
        });
    }
    setTrayIcon(url, cb) {
        this.logStatus("setTrayIcon", "deprecated");
        console.error("setTrayIcon() has been deprecated. See https://docs.interop.io/desktop/getting-started/how-to/rebrand-io-connect/user-interface/index.html#icons-tray");
    }
    launchExternalProcess() {
        this.logStatus("launchExternalProcess", "deprecated");
        console.error("launchExternalProcess() is no longer supported. Instead, configure an app config that points to the desired path of your executable and call `iop.appManager.application(name).start()`");
    }
    terminateExternalProcess() {
        this.logStatus("terminateExternalProcess", "deprecated");
        console.error("terminateExternalProcess() is no longer supported.");
    }
    async hideSplashScreen() {
        this.logStatus("hideSplashScreen", "start");
        await this.asyncConstructors.ready();
        const { err } = await toGoLangInvoke(() => this.glue.interop.invoke("T42.GD.Execute", { command: "hide-splash-screen" }));
        if (err) {
            this.logStatus("hideSplashScreen", "error", err);
        }
        this.logStatus("hideSplashScreen", "return");
    }
    async exit() {
        this.logStatus("exit", "start");
        await this.asyncConstructors.ready();
        await this.glue.appManager.exit({
            showDialog: false,
            autoSave: false
        });
        this.logStatus("exit", "return");
    }
    async getProcessList(cb) {
        this.logStatus("getProcessList", "start");
        await this.asyncConstructors.ready();
        const result = await this.glue.interop.invoke("T42.Telemetry", {
            command: "GetEvents",
            config: { stores: true, system: true, apps: true }
        });
        const data = result.returned;
        const apps = data?.data?.system?.perfData?.apps ?? [];
        const metrics = apps.map((app) => {
            return {
                cpuUsage: app.cpu?.percentCPUUsage,
                name: app.name,
                type: app.type,
                peakWorkingSetSize: app.memoryKB,
                processId: app.pid,
                workingSetSize: app.memoryKB,
                webContents: {}
            };
        });
        cb?.(metrics);
        this.logStatus("getProcessList", "return");
        return metrics;
    }
    async clearCache() {
        this.logStatus("clearCache", "start");
        await this.asyncConstructors.ready();
        const globalAny = globalThis;
        await globalAny.glue42gd.clearCache();
        this.logStatus("clearCache", "return");
    }
    async showDeveloperTools(name, viewId = true) {
        this.logStatus("showDeveloperTools", "start", { name, viewId });
        await this.asyncConstructors.ready();
        const inst = this.glue.appManager.instances().find(({ _id }) => _id === name);
        inst?.window.showDevTools();
        if (viewId !== null && viewId !== true) {
            this.loggerClient.warn("The use of the 'viewId' parameter has been deprecated in showDeveloperTools().");
        }
        this.logStatus("showDeveloperTools", "return");
    }
    async addEventListener(eventName, cb) {
        this.logStatus("addEventListener", "start", { eventName });
        await this.asyncConstructors.ready();
        if (eventName === "protocol-handler-triggered") {
            this.protocolHandlerEventChannel = uuid();
            this.loggerClient.debug(`FSBL.System.addEventListener("protocol-handler-triggered") channel = ${this.protocolHandlerEventChannel}`);
            this.glue.interop.register(this.protocolHandlerEventChannel, (params) => {
                cb?.(params.command);
            });
            const { err } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Event.add.protocol-handler-triggered", { invoker: this.protocolHandlerEventChannel }));
            if (err) {
                this.logStatus("addEventListener", "error", err);
            }
            this.logStatus("addEventListener", "return");
            return;
        }
        this.loggerClient.warn("FSBL.System.addEventListener called for unsupported event", eventName);
        this.logStatus("addEventListener", "return");
    }
    addSelectClientCertificateListener() {
        this.logStatus("addSelectClientCertificateListener", "deprecated");
        console.warn("addSelectClientCertificateListener() is deprecated. Client certificate selection is now part of io.CD's UI.");
    }
    addCertificateErrorListener() {
        this.logStatus("addCertificateErrorListener", "deprecated");
        console.warn("addSelectClientCertificateListener() is deprecated. Client certificate selection is now part of io.CD's UI.");
    }
    async getFEAInfo(cb) {
        this.logStatus("getFEAInfo", "start");
        await this.asyncConstructors.ready();
        const globalAny = globalThis;
        const result = {
            "@interopio/desktop": this.glue.version,
            "io.Connect Desktop": globalAny.glue42gd?.version,
            Electron: globalAny.glue42gd?.electron,
            FEA: globalAny.glue42gd?.version,
            isDevLaunch: false,
            sddEnabled: false,
            isDevMode: false,
        };
        this.logStatus("getFEAInfo", "return", result);
        cb?.(result);
        return result;
    }
    async getUpdateAvailable(cb) {
        this.loggerClient.error("getUpdateAvailable() is not yet implemented in the polyfill.");
        await this.asyncConstructors.ready();
        cb(false);
        return false;
    }
    async openUrlWithBrowser(url, cb) {
        this.logStatus("openUrlWithBrowser", "start", { url });
        await this.asyncConstructors.ready();
        const { err } = await openUrlWithBrowser(this.glue, url);
        if (err) {
            this.logStatus("openUrlWithBrowser", "error", err);
        }
        cb?.(err);
        this.logStatus("openUrlWithBrowser", "return");
    }
    async getProtocolString(cb) {
        this.logStatus("getProtocolString", "start");
        await this.asyncConstructors.ready();
        const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.System.getProtocolString", {}));
        if (err) {
            this.logStatus("getProtocolString", "error", err);
        }
        cb?.(data?.command);
        this.logStatus("getProtocolString", "return", data?.command);
        return data?.command;
    }
    async terminate() {
        this.logStatus("terminate", "start");
        await this.exit();
        this.logStatus("terminate", "return");
    }
    async restart() {
        this.logStatus("restart", "start");
        await this.asyncConstructors.ready();
        await this.glue.appManager.restart({
            showDialog: false,
            autoSave: false
        });
        this.logStatus("restart", "return");
    }
    async getEnvironmentVariable(envVar, cb) {
        this.logStatus("getEnvironmentVariable", "start", { envVar });
        await this.asyncConstructors.ready();
        const globalAny = globalThis;
        const env = globalAny.glue42gd?.env ?? {};
        const val = env[envVar];
        this.logStatus("getEnvironmentVariable", "return", val);
        cb?.(val);
        return val;
    }
    async getHostSpecs(cb) {
        this.logStatus("getHostSpecs", "start");
        await this.asyncConstructors.ready();
        const env = window?.glue42gd?.env ?? {};
        const hostSpecs = {
            env,
            hostname: env["HOSTNAME"]
        };
        this.logStatus("getHostSpecs", "return", hostSpecs);
        cb?.(hostSpecs);
        return hostSpecs;
    }
}
